/**
 * AuthCallback Page
 * HISI-FE-AUTH-01: Handles OAuth PKCE callback with cookie-based authentication
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { joinApiPath } from '@/lib/api-config';
import { supabase } from '@/lib/supabase';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Read code from query params first (PKCE flow)
      const code = new URLSearchParams(window.location.search).get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Determine safe redirect target (path-only)
      const requested = searchParams.get('redirect_to') || '/dashboard';
      const redirectTo = requested.startsWith('/') ? requested : '/dashboard';

      // Generate client-side trace ID for correlation with backend logs
      const clientTraceId = `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // ALWAYS log callback entry for debugging
      console.group('🔍 AuthCallback: Processing OAuth callback');
      console.log('📍 Current URL:', window.location.href);
      console.log('🔗 Search params:', Object.fromEntries(searchParams));
      console.log('🆔 Client Trace ID:', clientTraceId);
      console.log('📊 Flow detection:', {
        hasCode: !!code,
        codeLength: code?.length || 0,
        codePreview: code ? `${code.substring(0, 20)}...` : null,
        state,
        error,
        errorDescription
      });
      console.log('🎯 Redirect target:', redirectTo);
      console.groupEnd();

      try {
        if (error) {
          console.error('OAuth error:', error, errorDescription);
          navigate(`/login?error=${encodeURIComponent(error)}`, { replace: true });
          return;
        }

        if (code) {
          // Log current localStorage state before any auth operations
          const supabaseVerifierPre = localStorage.getItem('supabase.auth.code_verifier');
          const customVerifierPre = localStorage.getItem('pkce_verifier');

          console.log('🔍 Pre-auth localStorage state:', {
            supabaseVerifier: {
              exists: !!supabaseVerifierPre,
              preview: supabaseVerifierPre?.substring(0, 20) + '...' || 'null'
            },
            customVerifier: {
              exists: !!customVerifierPre,
              hasVerifier: customVerifierPre ? !!JSON.parse(customVerifierPre).verifier : false,
              hasExpiresAt: customVerifierPre ? !!JSON.parse(customVerifierPre).expiresAt : false
            },
            hasToken: !!localStorage.getItem('sb-yjjcfxqbuuyupmptaqmu-auth-token'),
            allKeys: Object.keys(localStorage).filter(k => k.includes('supabase') || k.includes('sb-') || k.includes('pkce'))
          });

          // PRIORITY FIX: Check for existing session first before any manual PKCE
          try {
            console.log('🎯 Priority check: Looking for existing Supabase session...');
            const { data: existingSession } = await supabase.auth.getSession();
            const hasExistingSession = !!existingSession?.session?.access_token;

            if (hasExistingSession) {
              console.log('✅ Found existing session - converting to cookies immediately (skipping events/manual PKCE)');

              // Convert session to HttpOnly cookies via backend
              console.log('🔄 Converting session to cookies...');
              const apiUrl = joinApiPath('auth', 'implicit-callback');
              const response = await fetch(apiUrl, {
                method: 'POST',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  access_token: existingSession.session!.access_token,
                  refresh_token: existingSession.session!.refresh_token,
                  state
                })
              });

              if (response.ok) {
                console.log('✅ Existing session converted to cookies successfully');

                // Note: We intentionally don't clear the local Supabase session here
                // because signOut({ scope: 'local' }) still invalidates server tokens
                // The cookie-based auth will take precedence anyway
                console.log('🧹 Keeping local session (cookies will take precedence)');

                // Navigate to dashboard with clean URL
                const redirectTo = new URLSearchParams(window.location.search).get('redirect_to') || '/dashboard';
                const cleanRedirectTo = redirectTo.startsWith('/') ? redirectTo : '/dashboard';

                // Strip URL params and redirect
                window.history.replaceState({}, '', window.location.pathname);
                window.location.href = cleanRedirectTo;
                return;
              }
            }

            console.log('🔍 No existing session found - proceeding with event waiting + manual fallback');
          } catch (existingSessionError) {
            console.log('⚠️ Could not check existing session, proceeding with normal flow:', existingSessionError);
          }

          // Wait for Supabase's automatic SIGNED_IN event first (preferred path)
          const signInWaitMs = 1500; // HISI-FE-AUTH-02: Extended from 1200ms for stability
          console.log('🎯 Waiting for SIGNED_IN event or manual exchange...', {
            timeoutMs: signInWaitMs,
            codeLength: code.length,
            hasVerifierForManualFallback: !!localStorage.getItem('supabase.auth.code_verifier')
          });

          const sessionFromEvent = await new Promise<any | null>((resolve) => {
            let eventCount = 0;
            const startTime = Date.now();

            const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
              eventCount++;
              const elapsed = Date.now() - startTime;

              console.log('🔔 Auth state change event:', {
                eventNumber: eventCount,
                event,
                hasSession: !!session,
                hasAccessToken: !!session?.access_token,
                elapsed: `${elapsed}ms`,
                sessionPreview: session ? {
                  user_id: session.user?.id,
                  expires_at: session.expires_at,
                  token_type: session.token_type
                } : null
              });

              // Log localStorage state during event
              console.log('🔍 localStorage during event:', {
                hasVerifier: !!localStorage.getItem('supabase.auth.code_verifier'),
                hasToken: !!localStorage.getItem('sb-yjjcfxqbuuyupmptaqmu-auth-token'),
                eventType: event
              });

              if (event === 'SIGNED_IN' && session?.access_token) {
                try { sub.subscription.unsubscribe(); } catch {}
                console.log('✅ SIGNED_IN event received with session', {
                  elapsed: `${elapsed}ms`,
                  accessTokenLength: session.access_token.length,
                  hasRefreshToken: !!session.refresh_token
                });
                resolve(session);
              }
            });

            setTimeout(() => {
              try { sub.subscription.unsubscribe(); } catch {}
              console.log('⏱️ SIGNED_IN event timeout reached, proceeding to manual exchange', {
                elapsed: `${Date.now() - startTime}ms`,
                eventsReceived: eventCount,
                hasVerifierForManual: !!localStorage.getItem('supabase.auth.code_verifier')
              });
              resolve(null);
            }, signInWaitMs);
          });

          let session = sessionFromEvent;

          // Fallback: single manual exchange (only if fresh verifier exists)
          if (!session) {
            console.log('🔄 Checking if manual PKCE exchange is viable...');

            // Log verifier state just before manual exchange
            const supabaseVerifier = localStorage.getItem('supabase.auth.code_verifier');
            const customVerifier = localStorage.getItem('pkce_verifier');

            // Check if custom verifier is fresh and not expired
            let customVerifierValid = false;
            if (customVerifier) {
              try {
                const { expiresAt } = JSON.parse(customVerifier);
                customVerifierValid = Date.now() < expiresAt;
              } catch {
                customVerifierValid = false;
              }
            }

            console.log('🔍 Verifier state before manual exchange:', {
              supabaseVerifier: {
                exists: !!supabaseVerifier,
                length: supabaseVerifier?.length || 0,
                preview: supabaseVerifier?.substring(0, 20) + '...' || 'null'
              },
              customVerifier: {
                exists: !!customVerifier,
                valid: customVerifierValid,
                parsed: customVerifier ? JSON.parse(customVerifier) : null,
                preview: customVerifier ? JSON.parse(customVerifier).verifier?.substring(0, 20) + '...' : 'null'
              },
              codeLength: code.length,
              codePreview: code.substring(0, 20) + '...'
            });

            // Gate manual exchange: only proceed if we have a usable verifier
            if (!supabaseVerifier && !customVerifierValid) {
              console.warn('⚠️ No valid verifier available - skipping manual PKCE exchange');
              console.log('🔄 Checking for existing session for conversion instead...');

              const { data: fallbackSession } = await supabase.auth.getSession();
              if (fallbackSession?.session?.access_token) {
                console.log('✅ Found existing session during fallback - converting to cookies');
                session = fallbackSession.session;
              } else {
                console.error('❌ No verifier and no existing session - authentication failed');
                setError('auth_failed');
                return;
              }
            } else {
              // If Supabase's verifier is missing but we have our custom one, restore it
              if (!supabaseVerifier && customVerifierValid && customVerifier) {
                try {
                  const { verifier } = JSON.parse(customVerifier);
                  console.log('🔄 Restoring missing Supabase verifier from valid custom storage');
                  localStorage.setItem('supabase.auth.code_verifier', verifier);
                } catch (parseError) {
                  console.error('❌ Failed to parse custom verifier:', parseError);
                  setError('auth_failed');
                  return;
                }
              }

              console.log('✅ Proceeding with manual PKCE exchange...');
              const exchangeStartTime = Date.now();
              const { data, error: sessionError } = await supabase.auth.exchangeCodeForSession(code);
              const exchangeElapsed = Date.now() - exchangeStartTime;

              // Log verifier state after manual exchange attempt
              const supabaseVerifierAfter = localStorage.getItem('supabase.auth.code_verifier');
              const customVerifierAfter = localStorage.getItem('pkce_verifier');

              console.log('🔍 Verifier state after manual exchange:', {
                supabaseVerifier: {
                  exists: !!supabaseVerifierAfter,
                  length: supabaseVerifierAfter?.length || 0,
                  changed: supabaseVerifier !== supabaseVerifierAfter
                },
                customVerifier: {
                  exists: !!customVerifierAfter,
                  changed: customVerifier !== customVerifierAfter
                },
                exchangeElapsed: `${exchangeElapsed}ms`
              });

              if (sessionError) {
                console.error('❌ Manual PKCE exchange failed:', {
                  error: sessionError.message,
                  code: sessionError.status,
                  clientTraceId,
                  exchangeElapsed: `${exchangeElapsed}ms`,
                  verifierState: {
                    supabaseBefore: !!supabaseVerifier,
                    supabaseAfter: !!supabaseVerifierAfter,
                    customBefore: !!customVerifier,
                    customAfter: !!customVerifierAfter,
                    restoredVerifier: !supabaseVerifier && customVerifier
                  }
                });

                // HISI-FE-AUTH-02: Optional server-side exchange fallback (feature-flagged)
                if (import.meta.env.VITE_ENABLE_SERVER_PKCE_FALLBACK === 'true') {
                  console.warn('🔄 Attempting server-side PKCE exchange fallback...');
                  try {
                    // Extract code_verifier from localStorage (last resort)
                    const codeVerifier = localStorage.getItem('supabase.auth.code_verifier');
                    if (codeVerifier) {
                      console.log('📤 Server-side PKCE exchange with extracted verifier');
                      const serverResponse = await fetch(joinApiPath('auth', 'callback'), {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          code,
                          code_verifier: codeVerifier,
                          state
                        })
                      });

                      if (serverResponse.ok) {
                        console.log('✅ Server-side PKCE exchange successful');
                        // Clear local session since server set cookies
                        console.log('🧹 Server-side PKCE: Clearing local session...');
                        await supabase.auth.signOut({ scope: 'local' });

                        // Strip code from URL and redirect
                        try {
                          const cleanUrl = `${window.location.origin}/auth/callback?redirect_to=${encodeURIComponent(redirectTo)}`;
                          window.history.replaceState({}, document.title, cleanUrl);
                          console.log('🔗 Server-side PKCE: URL cleaned');
                        } catch (urlError) {
                          console.warn('⚠️ Server-side PKCE: URL cleanup failed:', urlError);
                        }

                        // Brief delay to ensure server-set cookies propagate
                        console.log('⏱️ Server-side PKCE: Waiting for cookie propagation...');
                        await new Promise(resolve => setTimeout(resolve, 200));

                        // Verify server-side session works
                        console.log('🔍 Server-side PKCE: Verifying session...');
                        try {
                          const verifyResponse = await fetch(joinApiPath('auth', 'session'), {
                            method: 'GET',
                            credentials: 'include',
                            headers: {
                              'Content-Type': 'application/json'
                            }
                          });

                          if (!verifyResponse.ok) {
                            console.error('❌ Server-side PKCE: Session verification failed', {
                              status: verifyResponse.status,
                              clientTraceId
                            });
                            navigate('/login?error=server_session_verify_failed', { replace: true });
                            return;
                          }

                          console.log('✅ Server-side PKCE: Session verification successful', { clientTraceId });
                        } catch (verifyError) {
                          console.error('❌ Server-side PKCE: Session verification error:', verifyError);
                          navigate('/login?error=server_session_verify_failed', { replace: true });
                          return;
                        }

                        console.log('🎉 Server-side PKCE callback completed, redirecting...', {
                          redirectTo,
                          clientTraceId
                        });
                        navigate(redirectTo, { replace: true });
                        return;
                      } else {
                        const errorText = await serverResponse.text().catch(() => 'Unknown server error');
                        console.error('❌ Server-side PKCE exchange failed:', {
                          status: serverResponse.status,
                          error: errorText,
                          clientTraceId
                        });
                      }
                    } else {
                      console.warn('⚠️ No code_verifier found in localStorage for server-side exchange');
                    }
                  } catch (serverError) {
                    console.error('❌ Server-side PKCE exchange error:', serverError);
                  }
                }

                navigate('/login?error=session_exchange_failed', { replace: true });
                return;
              }
              session = data.session ?? null;
              console.log('✅ Manual PKCE exchange successful');
            }
          }

          if (!session || !session.access_token) {
            navigate('/login?error=no_session', { replace: true });
            return;
          }

          // Convert session to HttpOnly cookies via backend
          console.log('🔄 Converting session to cookies...');
          const startTime = Date.now();
          const apiUrl = joinApiPath('auth', 'implicit-callback');
          const response = await fetch(apiUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              access_token: session.access_token,
              refresh_token: session.refresh_token,
              state
            })
          });

          const elapsed = Date.now() - startTime;
          console.log('🍪 Cookie conversion request completed:', {
            elapsed: `${elapsed}ms`,
            status: response.status,
            statusText: response.statusText,
            clientTraceId
          });

          // Enhanced error diagnostics for cookie conversion
          const backendTraceId = response.headers.get('X-Auth-Trace-Id');
          console.log('🔗 Cookie conversion trace correlation:', {
            clientTraceId,
            backendTraceId,
            status: response.status,
            statusText: response.statusText
          });

          if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown cookie conversion error');
            console.error('❌ Cookie conversion failed:', {
              status: response.status,
              statusText: response.statusText,
              error: errorText,
              clientTraceId,
              backendTraceId,
              elapsed: `${elapsed}ms`,
              url: apiUrl,
              hasCorrelationHeader: !!backendTraceId
            });

            // Check if it's a specific type of error
            if (response.status === 401) {
              console.error('   → 401: Backend rejected the session tokens');
            } else if (response.status === 403) {
              console.error('   → 403: Permission denied during cookie conversion');
            } else if (response.status >= 500) {
              console.error('   → 5xx: Backend server error during cookie conversion');
            } else if (response.status === 0) {
              console.error('   → Network error: Request was blocked or aborted');
            }

            navigate('/login?error=cookie_conversion_failed', { replace: true });
            return;
          }

          console.log('✅ Session converted to secure cookies successfully', {
            clientTraceId,
            backendTraceId
          });

          // Clear local Supabase session
          console.log('🧹 Clearing local Supabase session...');
          await supabase.auth.signOut({ scope: 'local' });

          // Strip code from the URL to avoid re-processing
          try {
            const cleanUrl = `${window.location.origin}/auth/callback?redirect_to=${encodeURIComponent(redirectTo)}`;
            window.history.replaceState({}, document.title, cleanUrl);
            console.log('🔗 URL cleaned, code parameter removed');
          } catch (urlError) {
            console.warn('⚠️ URL cleanup failed:', urlError);
          }

          // Brief delay to ensure cookies propagate before redirect
          console.log('⏱️ Waiting for cookie propagation before redirect...');
          await new Promise(resolve => setTimeout(resolve, 200));

          // Verify session works before redirect
          console.log('🔍 Verifying session is accessible via cookies...');
          try {
            const verifyResponse = await fetch(joinApiPath('auth', 'session'), {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json'
              }
            });

            console.log('🔍 Session verification result:', {
              status: verifyResponse.status,
              statusText: verifyResponse.statusText,
              hasSetCookie: !!verifyResponse.headers.get('set-cookie'),
              clientTraceId,
              backendTraceId
            });

            if (!verifyResponse.ok) {
              console.error('❌ Session verification failed - cookies not working', {
                status: verifyResponse.status,
                clientTraceId,
                backendTraceId
              });
              navigate('/login?error=session_verify_failed', { replace: true });
              return;
            }

            const sessionData = await verifyResponse.json();
            console.log('✅ Session verification successful', {
              hasUser: !!sessionData.user,
              userId: sessionData.user?.id,
              clientTraceId,
              backendTraceId
            });
          } catch (verifyError) {
            console.error('❌ Session verification network error:', {
              error: verifyError instanceof Error ? verifyError.message : String(verifyError),
              clientTraceId,
              backendTraceId
            });
            navigate('/login?error=session_verify_failed', { replace: true });
            return;
          }

          // Success → redirect
          console.log('🎉 OAuth callback completed successfully, redirecting...', {
            redirectTo,
            clientTraceId,
            backendTraceId
          });
          navigate(redirectTo, { replace: true });
          return;
        } else {
          // No code - check for implicit flow fallback (feature-flagged)
          const hasImplicitTokens = window.location.hash.includes('access_token=');

          // Also check for existing Supabase session (tokens already processed and hash cleared)
          const { data: existingSession } = await supabase.auth.getSession();
          const hasExistingSession = !!existingSession?.session?.access_token;

          console.log('🔍 Session detection state:', {
            hasImplicitTokens,
            hasExistingSession,
            existingSessionPreview: existingSession?.session ? {
              hasAccessToken: !!existingSession.session.access_token,
              hasRefreshToken: !!existingSession.session.refresh_token,
              userId: existingSession.session.user?.id,
              expiresAt: existingSession.session.expires_at
            } : null,
            implicitFallbackEnabled: import.meta.env.VITE_ALLOW_IMPLICIT_FALLBACK
          });

          if ((hasImplicitTokens || hasExistingSession) && import.meta.env.VITE_ALLOW_IMPLICIT_FALLBACK === 'true') {
            // Temporary escape hatch: implicit flow fallback
            console.warn('⚠️ Using implicit flow fallback (temporary)');

            // Extract tokens from hash OR existing session
            let accessToken, refreshToken;

            if (hasImplicitTokens) {
              // Extract from URL hash
              const fragment = window.location.hash.substring(1);
              const fragmentParams = new URLSearchParams(fragment);
              accessToken = fragmentParams.get('access_token');
              refreshToken = fragmentParams.get('refresh_token');
              console.log('📦 Implicit Fallback: Using hash tokens');
            } else if (hasExistingSession && existingSession?.session) {
              // Extract from existing Supabase session
              accessToken = existingSession.session.access_token;
              refreshToken = existingSession.session.refresh_token;
              console.log('📦 Implicit Fallback: Using existing session tokens');
            }

            if (accessToken) {
              console.log('🔄 Implicit Fallback: Converting tokens to backend session...');

              const response = await fetch(joinApiPath('auth', 'implicit-callback'), {
                method: 'POST',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  access_token: accessToken,
                  refresh_token: refreshToken,
                  state
                })
              });

              if (!response.ok) {
                console.error('❌ Implicit fallback failed', {
                  status: response.status
                });
                setError('callback_error');
                return;
              }

              console.log('✅ Implicit fallback: Session converted to secure cookies');

              // Clear localStorage session after successful conversion to prevent drift
              console.log('🧹 Clearing localStorage Supabase session after conversion...');
              await supabase.auth.signOut({ scope: 'local' }); // Clear only local storage, not backend

              console.log('🎉 Implicit fallback completed, redirecting...', {
                redirectTo,
                clientTraceId
              });
            } else {
              console.error('❌ Implicit fallback: No access token found');
              setError('missing_code');
              return;
            }
          } else {
            // No code and no implicit fallback
            console.error('❌ No authorization code in callback');
            console.error('   URL:', window.location.href);
            console.error('   Expected: ?code=... for PKCE flow');
            if (hasImplicitTokens) {
              console.error('   Implicit tokens detected but fallback disabled');
            }
            setError('missing_code');
            return;
          }
        }

        // Success → redirect
        navigate(redirectTo, { replace: true });
      } catch (err) {
        console.error('Auth callback handling failed:', err);
        setError('callback_error');
      }
    };

    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show error state with retry CTA
  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
            <span className="text-red-400 text-xl">⚠</span>
          </div>
          <h2 className="text-xl font-semibold mb-2">Authentication Failed</h2>
          <p className="text-white/70 font-mono text-sm mb-6">
            {error === 'missing_code' ?
              'No authorization code was received from the login provider. Expected PKCE code flow but got missing code.' :
            error === 'session_exchange_failed' ?
              'Failed to exchange authorization code for session. This may indicate a PKCE configuration issue.' :
            error === 'no_session' ?
              'No session was returned after code exchange. Authentication may have failed.' :
            error === 'cookie_conversion_failed' ?
              'Failed to convert session to secure cookies. Backend authentication error.' :
            error === 'implicit_deprecated' ?
              'Deprecated implicit flow detected. OAuth configuration needs to be updated to use PKCE code flow instead of implicit tokens.' :
              'An error occurred during the login process.'
            }
          </p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Try Login Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/70 font-mono text-sm">Signing you in…</p>
      </div>
    </div>
  );
}